import { useCallback, useRef } from 'react';
import {
  analyticsRiderProfileAbandoned,
  analyticsRiderProfileFieldsEdited,
  analyticsRiderProfileSave,
  analyticsRiderProfileSaveAttempted,
  analyticsRiderProfileSectionViewed,
  analyticsRiderProfileUnsavedChangesModalConfirmed,
  analyticsRiderProfileUnsavedChangesModalDismissed,
  analyticsRiderProfileUnsavedChangesModalShown,
} from '@rideiq/api/services/Analytics';

import { trackFullStoryEvent } from '../../../../vendor/features/FullStory/FullStory.service';
import { RiderFormValues, SECTION_KEYS, SectionKey } from '../types';

const SECTION_NAME_MAP: Record<SectionKey, string> = {
  'basic-info': 'basic_info',
  contacts: 'contacts',
  locations: 'locations',
  'rider-services': 'rider_services',
};

type UseRiderFormAnalyticsArgs = {
  action: 'create' | 'edit';
  slug: string | undefined;
};

export type UseRiderFormAnalyticsReturn = {
  reportSectionViewed: (section: SectionKey) => void;
  reportSaveAttempted: (hasValidationErrors: boolean) => void;
  reportSave: (structuredFieldsSet: string[], hasMustBeMet: boolean, sisImportUsed: boolean) => void;
  reportAbandoned: (lastActiveSection: SectionKey) => void;
  trackDirtyFields: (dirtyFields: Partial<Record<keyof RiderFormValues, unknown>>) => void;
  reportUnsavedChangesModalShown: () => void;
  reportUnsavedChangesModalConfirmed: () => void;
  reportUnsavedChangesModalDismissed: () => void;
  reportFieldsEdited: () => void;
};

export const useRiderFormAnalytics = ({ action }: UseRiderFormAnalyticsArgs): UseRiderFormAnalyticsReturn => {
  const visitedSectionsRef = useRef<Set<SectionKey>>(new Set());
  const openedAtRef = useRef<number>(Date.now());
  const editedFieldsRef = useRef<Set<keyof RiderFormValues>>(new Set());

  const reportSectionViewed = useCallback(
    (section: SectionKey) => {
      if (visitedSectionsRef.current.has(section)) {
        return;
      }
      visitedSectionsRef.current.add(section);
      analyticsRiderProfileSectionViewed({ section_name: SECTION_NAME_MAP[section], action });
      trackFullStoryEvent('rider_profile_step_viewed', {
        step_name: section,
        step_number: SECTION_KEYS.indexOf(section) + 1,
      });
    },
    [action]
  );

  const reportSaveAttempted = useCallback(
    (hasValidationErrors: boolean) => {
      analyticsRiderProfileSaveAttempted({ action, has_validation_errors: hasValidationErrors });
    },
    [action]
  );

  const reportSave = useCallback(
    (structuredFieldsSet: string[], hasMustBeMet: boolean, sisImportUsed: boolean) => {
      const sectionsVisited = Array.from(visitedSectionsRef.current).map((s) => SECTION_NAME_MAP[s]);
      const timeInFormSeconds = Math.round((Date.now() - openedAtRef.current) / 1000);

      analyticsRiderProfileSave({
        action,
        sections_visited: sectionsVisited,
        structured_fields_set: structuredFieldsSet,
        time_in_form_seconds: timeInFormSeconds,
        sis_import_used: sisImportUsed,
      });

      if (action === 'create') {
        trackFullStoryEvent('rider_profile_create_completed', {
          has_safety_settings: structuredFieldsSet.length > 0,
          setting_count: structuredFieldsSet.length,
          has_must_be_met: hasMustBeMet,
        });
      } else {
        trackFullStoryEvent('rider_profile_edit_saved', { sections_modified: sectionsVisited });
      }
    },
    [action]
  );

  const reportAbandoned = useCallback(
    (lastActiveSection: SectionKey) => {
      const timeInFormSeconds = Math.round((Date.now() - openedAtRef.current) / 1000);

      analyticsRiderProfileAbandoned({
        action,
        last_active_section: SECTION_NAME_MAP[lastActiveSection],
        time_in_form_seconds: timeInFormSeconds,
      });

      if (action === 'create') {
        trackFullStoryEvent('rider_profile_create_abandoned', {
          last_step_viewed: lastActiveSection,
          time_spent_seconds: timeInFormSeconds,
        });
      }
    },
    [action]
  );

  const trackDirtyFields = useCallback((dirtyFields: Partial<Record<keyof RiderFormValues, unknown>>) => {
    Object.keys(dirtyFields).forEach((key) => editedFieldsRef.current.add(key as keyof RiderFormValues));
  }, []);

  const reportUnsavedChangesModalShown = useCallback(() => {
    analyticsRiderProfileUnsavedChangesModalShown({ action });
  }, [action]);

  const reportUnsavedChangesModalConfirmed = useCallback(() => {
    analyticsRiderProfileUnsavedChangesModalConfirmed({ action });
  }, [action]);

  const reportUnsavedChangesModalDismissed = useCallback(() => {
    analyticsRiderProfileUnsavedChangesModalDismissed({ action });
  }, [action]);

  const reportFieldsEdited = useCallback(() => {
    analyticsRiderProfileFieldsEdited({ action, fields_edited: Array.from(editedFieldsRef.current) });
  }, [action]);

  return {
    reportSectionViewed,
    reportSaveAttempted,
    reportSave,
    reportAbandoned,
    trackDirtyFields,
    reportUnsavedChangesModalShown,
    reportUnsavedChangesModalConfirmed,
    reportUnsavedChangesModalDismissed,
    reportFieldsEdited,
  };
};
